import type { NutritionTemplate, DayPlan, Meal, FoodInMeal } from '../types/nutrition';

export type NutritionTotals = { calories: number; protein: number; carbs: number; fat: number };

export const ZERO: NutritionTotals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

export function percentToGrams(kcal: number, pct: number, divisor: 4 | 9): number {
  if (!kcal || !pct) return 0;
  return (kcal * (pct / 100)) / divisor;
}

export function computeTemplateTargets(t: NutritionTemplate): NutritionTotals {
  const kcal = t?.targetNutrition?.dailyCalories || 0;
  const p = t?.targetNutrition?.proteinPercentage || 0;
  const c = t?.targetNutrition?.carbsPercentage || 0;
  const f = t?.targetNutrition?.fatPercentage || 0;
  return {
    calories: Math.round(kcal),
    protein: Number(percentToGrams(kcal, p, 4).toFixed(1)),
    carbs: Number(percentToGrams(kcal, c, 4).toFixed(1)),
    fat: Number(percentToGrams(kcal, f, 9).toFixed(1)),
  };
}

export function sumMealTotals(meals: Array<{ totalNutrition?: NutritionTotals }> = []): NutritionTotals {
  return meals.reduce<NutritionTotals>(
    (acc, m) => {
      const n = m?.totalNutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 };
      return {
        calories: acc.calories + (n.calories || 0),
        protein: acc.protein + (n.protein || 0),
        carbs: acc.carbs + (n.carbs || 0),
        fat: acc.fat + (n.fat || 0),
      };
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );
}

export function effectiveDayActual(day: DayPlan): NutritionTotals {
  if (!day) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  if (day.dailyNutrition) return day.dailyNutrition as NutritionTotals;
  return sumMealTotals(day.meals);
}

// New calculators that always derive from foods
export function sumFoods(foods: FoodInMeal[] = []): NutritionTotals {
  return foods.reduce<NutritionTotals>(
    (t, f) => ({
      calories: t.calories + (f?.nutrition?.calories ?? 0),
      protein: t.protein + (f?.nutrition?.protein ?? 0),
      carbs: t.carbs + (f?.nutrition?.carbs ?? 0),
      fat: t.fat + (f?.nutrition?.fat ?? 0),
    }),
    { ...ZERO },
  );
}

export function computeMealTotals(meal: Meal): NutritionTotals {
  return sumFoods(meal?.foods ?? []);
}

export function computeDayTotals(day: DayPlan): NutritionTotals {
  if (!day?.meals?.length) return { ...ZERO };
  return day.meals.reduce<NutritionTotals>(
    (t, m) => {
      const mt = computeMealTotals(m);
      return {
        calories: t.calories + mt.calories,
        protein: t.protein + mt.protein,
        carbs: t.carbs + mt.carbs,
        fat: t.fat + mt.fat,
      };
    },
    { ...ZERO },
  );
}

/** Dev-only guard to spot stale stored totals */
export function warnIfOutOfSync(day: DayPlan) {
  // Only warn in dev
  try {
    const isDev = (import.meta as any).env?.DEV === true;
    if (!isDev) return;
  } catch {
    // OBS-11 a-ignore: dev-only debug logging must never break the real code path.
  }
  const calc = computeDayTotals(day);
  const stored = day?.dailyNutrition ?? ZERO;
  const delta = Math.abs(calc.calories - stored.calories);
  if (delta > 1) {
    // eslint-disable-next-line no-console
    console.warn('[nutrition] day totals out-of-sync', { dayId: (day as any)?.id, stored, calc });
  }
}

// Helper for computing meal totals from grams (pure helper)
export function computeMealTotalsFromGrams(items: Array<{ food: any; grams: number }>) {
  // Import macrosForGrams dynamically to avoid circular dependency
  const { macrosForGrams } = require('../services/foodData');

  return items.reduce(
    (acc, { food, grams }) => {
      const m = macrosForGrams(food, grams);
      acc.calories += m.kcal;
      acc.protein = +(acc.protein + m.protein_g).toFixed(1);
      acc.carbs = +(acc.carbs + m.carbs_g).toFixed(1);
      acc.fat = +(acc.fat + m.fat_g).toFixed(1);
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );
}
